import React, { useCallback, useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import css from './styles.module.scss';
import { Grid, Button, IconButton, LinearProgress } from '@material-ui/core';
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import { DealDocumentDto } from '~/models/deals/DealDocument.dto';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { selectDocumentTypes } from '~/features/dashboard/store/selectors';
import { getDealDocumentTypesThunk } from '~/features/dashboard/store/actions.thunks';
import { useThunkDispatch } from '~/utils/hooks/useThunkDispatch';
import { TextFieldOutlined } from '~/common/text-field-outlined';
import { axiosInstance } from '~/api/config/axios.config';
import Avatar from '@material-ui/core/Avatar';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { AutocompleteRenderInputParams } from '@material-ui/lab/Autocomplete/Autocomplete';
import {AddContactFormNames} from "~/store/user/models";

type FormInputsType = {
    id?: number;
    assetPath?: string;
    assetName: string;
    assetTags: string;
    assetVersion?: string;
};

type FileType = {
    name: string;
    type: string;
    path: string;
};

type Props = {
    types: string[];
    item?: DealDocumentDto;
    onSubmit(formData: FormInputsType): void;
};

// TODO: add file upload from system dialog
export const DocumentsRightDrawer: React.FC<Props> = ({ types, item, onSubmit }) => {
    const [file, setFile] = useState<FileType | undefined>();
    const [drag, setDrag] = useState(false);
    const [isLoading, setLoading] = useState(false);
    const [progress, setProgress] = React.useState(0);

    let size: number = 0;
    const config = {
        onUploadProgress: (progressEvent: { loaded: any }) => {
            setProgress(Math.min((progressEvent.loaded / size) * 100, 100));
        },
    };

    const dragStartHandler = (e: any) => {
        e.preventDefault();
        setDrag(true);
    };

    const dragLeaveHandler = (e: any) => {
        e.preventDefault();
        setDrag(false);
    };

    const dragOverHandler = (e: any) => {
        e.preventDefault();
        setDrag(true);
    };

    const onDropHandler = async (e: any) => {
        e.preventDefault();
        setLoading(true);
        const newFile = e.dataTransfer.files[0];
        const fd = new FormData();
        fd.append('file', newFile);
        axiosInstance
            .post('/catalogue/projectCreator/fileupload', fd, config)
            .then(({data: response}) => {
                setFile({
                    name: newFile.name,
                    type: newFile.type,
                    path: response[0].filePath,
                });
                setLoading(false);
            })
            .finally(() => {
                setDrag(false);
            });
    };

    const handleFileUpload = (e: any) => {
        const files = e.target.files;
        const fd = new FormData();
        const newFile = files[0];
        if (newFile) {
            setLoading(true);
            fd.append('file', newFile);
            axiosInstance
                .post('/catalogue/projectCreator/fileupload', fd, config)
                .then(({data: response}) => {
                    setFile({
                        name: newFile.name,
                        type: newFile.type,
                        path: response[0].filePath,
                    });
                    setLoading(false);
                })
                .finally(() => {
                    setDrag(false);
                });
        }
    };

    const { register, control, errors, setValue, handleSubmit, watch } = useForm<FormInputsType>({
        defaultValues: {
            id: item?.id,
            assetPath: item?.assetPath || '',
            assetName: item?.assetName || '',
            assetTags: item?.assetTags || '',
            assetVersion: item?.assetVersion || '',
        },
    });

    const currentFilePath = watch('assetPath');

    const downloadDocument = useCallback((): void => {
        if (!currentFilePath) {
            return;
        }
        const fileUrl = currentFilePath[0] === '/' ? `${process.env.REACT_APP_ASSET}${currentFilePath}` : currentFilePath;
        window.open(fileUrl, '_blank');
    }, [currentFilePath]);

    useEffect(() => {
        file && setValue('assetPath', file.path);
    }, [file, setValue]);

    return (
        <Grid component="form" onSubmit={handleSubmit(onSubmit)} container className={css.module} spacing={3}>
            <Grid item xs={12}>
                <Typography variant="h5">{item ? 'Edit' : 'Upload'} Document</Typography>
            </Grid>
            <Grid item xs={12}>
                <Box display="flex" justifyContent="center">
                    <input ref={register} name="id" type="hidden" />
                    <TextFieldOutlined
                        label="Name"
                        name="assetName"
                        error={!!errors.assetName}
                        helperText={errors.assetName?.message}
                        inputRef={register({ required: 'Required' })}
                        fullWidth
                    />
                </Box>
            </Grid>
            <Grid item xs={6}>
                <Box display="flex" justifyContent="center">
                    <TextFieldOutlined
                        label="Version"
                        name="assetVersion"
                        error={!!errors.assetVersion}
                        helperText={errors.assetVersion?.message}
                        inputRef={register}
                        fullWidth
                    />
                </Box>
            </Grid>
            <Grid item xs={6}>
                <Box display="flex" justifyContent="center">
                    <Controller
                        render={props => (
                            <Autocomplete
                                {...props}
                                options={types}
                                fullWidth
                                value={props.value}
                                renderInput={(p: AutocompleteRenderInputParams) => (
                                    <TextFieldOutlined {...p} label="Tag" error={!!errors.assetTags} helperText={errors.assetTags?.message} />
                                )}
                                onChange={(_, data) => props.onChange(data)}
                            />
                        )}
                        name="assetTags"
                        control={control}
                        rules={{
                            required: 'Required',
                        }}
                        fullWidth
                    />
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Box display="flex" alignItems="center">
                    <TextFieldOutlined
                        label="File"
                        name="assetPath"
                        error={!!errors.assetPath}
                        helperText={errors.assetPath?.message}
                        inputRef={register({ required: 'Required' })}
                        InputProps={{
                            readOnly: true,
                        }}
                        fullWidth
                    />
                    <Button
                        disabled={!currentFilePath}
                        onClick={downloadDocument}
                        variant="contained"
                        size="large"
                        color="primary"
                        type="button"
                        className={classNames(css.btnSave, css.btnDownload)}
                    >
                        Download
                    </Button>
                </Box>
                <Typography className={css.fieldName}>Drag and Drop Asset File</Typography>
                <Box className={css.dragDrop} onDragStart={dragStartHandler} onDragLeave={dragLeaveHandler} onDragOver={dragOverHandler}
                     onDrop={onDropHandler}>
                    {file?.path ? (
                        <>
                            <input
                                type="file"
                                name={AddContactFormNames.IMAGE}
                                id="file"
                                className={css.inputFile}
                                onChange={e => {
                                    handleFileUpload(e);
                                }}
                            />
                            <label htmlFor="file" className={css.labelFile}>
                                <Avatar alt={file.name} src={file.path} className={css.avatarCustom} />
                            </label>
                        </>
                    ) : (
                        <>
                            <input
                                type="file"
                                name={AddContactFormNames.IMAGE}
                                id="file"
                                className={css.inputFile}
                                onChange={e => {
                                    handleFileUpload(e);
                                }}
                            />

                            <IconButton className={css.dragDropIcon}>
                                <label htmlFor="file" className={css.labelFile}>
                                    <CloudDownloadIcon/>
                                </label>
                            </IconButton>
                        </>
                    )}
                    <Typography className={css.fieldName}>{isLoading ? 'Loading...' : drag ? 'Drop the file' : 'Drag and Drop'}</Typography>
                    {isLoading && <LinearProgress className={css.loading} variant="determinate" value={progress} />}
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Box display="flex" justifyContent="center">
                    <Button variant="contained" size="large" color="primary" type="submit" className={css.btnSave}>
                        Save
                    </Button>
                </Box>
            </Grid>
        </Grid>
    );
};

type ConnectedProps = Omit<Props, 'onSubmit' | 'types'> & {
    assetCode: string;
    onClose(): void;
};

const ConnectedDocumentsRightDrawer: React.FC<ConnectedProps> = ({ assetCode, onClose, ...props }) => {
    const dispatch = useThunkDispatch();

    const onSubmit = useCallback(
        (formData: FormInputsType): void => {
            axiosInstance
                .post('catalogue/projectCreator/dealtag/save', {
                    ...formData,
                    contractrefnum: assetCode,
                })
                .then(onClose);
        },
        [assetCode, onClose]
    );

    const documentTypes = useSelector(selectDocumentTypes);
    const types = useMemo(() => documentTypes.map(dt => dt.AssetTag), [documentTypes]);

    useEffect(() => {
        if (!types.length) {
            dispatch(getDealDocumentTypesThunk());
        }
    }, [dispatch, types]);

    return <DocumentsRightDrawer {...props} types={types} onSubmit={onSubmit} />;
};

export default ConnectedDocumentsRightDrawer;
